import type { AdminSessionContext } from '@kiosk/contracts';
import type { SyntheticEvent } from 'react';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { authorizeAdminSession, createAdminService, type AdminService } from './adminService';
import { inspectAdminEnvironment } from './environment';
import { ADMIN_ROUTES } from './routes';

const OverviewRoute = lazy(() =>
  import('./OverviewRoute').then((module) => ({ default: module.OverviewRoute })),
);
const SalesRoute = lazy(() =>
  import('./SalesRoute').then((module) => ({ default: module.SalesRoute })),
);
const InventoryRoute = lazy(() =>
  import('./InventoryRoute').then((module) => ({ default: module.InventoryRoute })),
);
const ResidentsRoute = lazy(() =>
  import('./ResidentsRoute').then((module) => ({ default: module.ResidentsRoute })),
);
const StaffRoute = lazy(() =>
  import('./StaffRoute').then((module) => ({ default: module.StaffRoute })),
);
const DevicesRoute = lazy(() =>
  import('./DevicesRoute').then((module) => ({ default: module.DevicesRoute })),
);

type AuthState =
  | { status: 'checking' }
  | { status: 'signed-out' }
  | { status: 'denied'; message: string }
  | { status: 'authorized'; identity: AdminSessionContext };

export function App() {
  const environment = inspectAdminEnvironment(import.meta.env);
  const service = useMemo(() => {
    if (!environment.configured) return null;
    return createAdminService(import.meta.env);
  }, [environment.configured]);
  const [auth, setAuth] = useState<AuthState>({ status: 'checking' });

  const authorize = useCallback(async (candidate: AdminService) => {
    try {
      setAuth(await authorizeAdminSession(candidate));
    } catch {
      setAuth({
        status: 'denied',
        message: 'The secure session could not be verified. Please sign in again.',
      });
    }
  }, []);

  useEffect(() => {
    if (service === null) return;
    queueMicrotask(() => void authorize(service));
    return service.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') setAuth({ status: 'signed-out' });
      else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED')
        queueMicrotask(() => void authorize(service));
    });
  }, [authorize, service]);

  if (!environment.configured || service === null)
    return <ConfigurationScreen message={environment.message} />;
  if (auth.status === 'checking') return <FullPageStatus label="Verifying secure access…" />;
  if (auth.status === 'signed-out' || auth.status === 'denied') {
    return (
      <SignInScreen
        service={service}
        environment={environment.identity}
        accessMessage={auth.status === 'denied' ? auth.message : null}
        onStart={() => setAuth({ status: 'checking' })}
        onFailure={(message) => setAuth({ status: 'denied', message })}
      />
    );
  }
  return (
    <AuthorizedApp identity={auth.identity} service={service} environment={environment.identity} />
  );
}

function AuthorizedApp({
  identity,
  service,
  environment,
}: {
  identity: AdminSessionContext;
  service: AdminService;
  environment: 'LOCAL' | 'STAGING' | 'PRODUCTION';
}) {
  const [location, setLocation] = useState(() => ({
    path: normalizedPath(window.location.pathname),
    search: window.location.search,
  }));
  useEffect(() => {
    const update = () =>
      setLocation({
        path: normalizedPath(window.location.pathname),
        search: window.location.search,
      });
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);
  const navigate = useCallback((nextPath: string) => {
    window.history.pushState({}, '', nextPath);
    setLocation({ path: normalizedPath(window.location.pathname), search: window.location.search });
  }, []);
  const route =
    ADMIN_ROUTES.find(
      (item) => location.path === item.path || location.path.startsWith(`${item.path}/`),
    ) ?? ADMIN_ROUTES[0];

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-lockup">
          <div className="brand-mark" aria-hidden="true">
            KC
          </div>
          <div>
            <p className="eyebrow">Kiosk Operations</p>
            <p className="brand-name">Kenzo Court</p>
          </div>
          {environment === 'STAGING' && <span className="environment-badge">STAGING</span>}
        </div>
        <nav aria-label="Administration">
          {ADMIN_ROUTES.map((item) => (
            <button
              className={item.path === route.path ? 'nav-item nav-item-active' : 'nav-item'}
              key={item.path}
              onClick={() => navigate(item.path)}
              type="button"
            >
              <span aria-hidden="true">{item.icon}</span>
              {item.label}
              {!item.available && (
                <span className="nav-lock" aria-label="Planned">
                  ·
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="sidebar-profile">
          <span className="avatar" aria-hidden="true">
            {initials(identity.displayName)}
          </span>
          <span>
            <strong>{identity.displayName}</strong>
            <small>{identity.role}</small>
          </span>
          <button
            className="icon-button dark"
            type="button"
            onClick={() => void service.signOut()}
            aria-label="Sign out"
          >
            ↪
          </button>
        </div>
      </aside>
      <main className="main-content">
        <div className="mobile-bar">
          <div className="brand-mark small" aria-hidden="true">
            KC
          </div>
          <strong>Kenzo Court</strong>
          {environment === 'STAGING' && <span className="environment-badge">STAGING</span>}
          <button className="text-button" type="button" onClick={() => void service.signOut()}>
            Sign out
          </button>
        </div>
        <div className="mobile-navigation" aria-label="Administration sections">
          {ADMIN_ROUTES.map((item) => (
            <button
              type="button"
              key={item.path}
              className={item.path === route.path ? 'active' : ''}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <Suspense fallback={<FullPageStatus label={`Loading ${route.label.toLowerCase()}…`} />}>
          {route.path === '/overview' && <OverviewRoute service={service} onNavigate={navigate} />}
          {route.path === '/sales' && (
            <SalesRoute
              service={service}
              path={location.path}
              search={location.search}
              onNavigate={navigate}
            />
          )}
          {route.path === '/inventory' && (
            <InventoryRoute
              service={service}
              path={location.path}
              search={location.search}
              onNavigate={navigate}
            />
          )}
          {route.path === '/residents' && (
            <ResidentsRoute
              service={service}
              path={location.path}
              search={location.search}
              onNavigate={navigate}
            />
          )}
          {route.path === '/staff' && (
            <StaffRoute
              service={service}
              identity={identity}
              path={location.path}
              search={location.search}
              onNavigate={navigate}
            />
          )}
          {route.path === '/devices' && (
            <DevicesRoute
              service={service}
              identity={identity}
              path={location.path}
              search={location.search}
              onNavigate={navigate}
            />
          )}
          {!route.available && <UnavailableRoute label={route.label} />}
        </Suspense>
      </main>
    </div>
  );
}

function SignInScreen({
  service,
  environment,
  accessMessage,
  onStart,
  onFailure,
}: {
  service: AdminService;
  environment: 'LOCAL' | 'STAGING' | 'PRODUCTION';
  accessMessage: string | null;
  onStart: () => void;
  onFailure: (message: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  async function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>) {
    event.preventDefault();
    setSubmitting(true);
    onStart();
    try {
      await service.signIn(email.trim(), password);
    } catch (error) {
      setSubmitting(false);
      onFailure(error instanceof Error ? error.message : 'Sign in failed.');
    }
  }
  return (
    <div className="auth-page">
      <section className="auth-story">
        <div className="brand-mark large" aria-hidden="true">
          KC
        </div>
        {environment === 'STAGING' && <span className="environment-badge">STAGING</span>}
        <p className="eyebrow">Kenzo Court</p>
        <h1>Operations, in clear view.</h1>
        <p>
          A secure workspace for the people responsible for the kiosk, its residents, and every
          recorded sale.
        </p>
      </section>
      <section className="auth-panel">
        <form className="auth-card" onSubmit={(event) => void submit(event)}>
          <p className="eyebrow ink">Administration</p>
          <h2>Welcome back</h2>
          <p className="muted">Sign in with an active Manager or Admin account.</p>
          {accessMessage !== null && (
            <div className="form-error" role="alert">
              {accessMessage}
            </div>
          )}
          <label>
            Email address
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <button className="primary-button" disabled={submitting} type="submit">
            {submitting ? 'Signing in…' : 'Sign in securely'}
          </button>
          <small>Authentication is handled by the property’s secure Supabase environment.</small>
        </form>
      </section>
    </div>
  );
}

function ConfigurationScreen({ message }: { message: string }) {
  return (
    <div className="center-page">
      <div className="brand-mark large">KC</div>
      <p className="eyebrow ink">Configuration required</p>
      <h1>Admin is not connected</h1>
      <p>{message}</p>
      <code>VITE_SUPABASE_URL · VITE_SUPABASE_ANON_KEY</code>
    </div>
  );
}
function FullPageStatus({ label }: { label: string }) {
  return (
    <div className="center-page" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <p>{label}</p>
    </div>
  );
}
function UnavailableRoute({ label }: { label: string }) {
  return (
    <section className="unavailable">
      <p className="eyebrow ink">Planned workspace</p>
      <h1>{label}</h1>
      <p>
        This area is intentionally reserved for a later approved phase. No placeholder operational
        data is shown.
      </p>
    </section>
  );
}
function normalizedPath(path: string): string {
  return ADMIN_ROUTES.some((route) => path === route.path || path.startsWith(`${route.path}/`))
    ? path
    : '/overview';
}
function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
